import { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Typography,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ConfirmationModal } from '@components';
import { theme } from '@store';
import { t } from 'i18next';
import { ConsentEntry } from './ConsentEntry';
import { useConsent } from '../hooks';

interface IConsentTabProps {
  consent: ReturnType<typeof useConsent>;
  programName: string;
  onClose: () => void;
  disabled?: boolean;
}

const tr = (key: string, options?: Record<string, unknown>) =>
  t(`patient.detailsCard.editPatientProgram.consent.${key}`, options ?? {});

export const ConsentTab = ({
  consent,
  programName,
  onClose,
  disabled = false,
}: IConsentTabProps) => {
  const {
    formMethods,
    fields,
    submitEntry,
    deleteEntry,
    handleAdd,
    canAdd,
    isSubmitting,
    dirtyEntries,
  } = consent;

  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);
  const [submittingIndex, setSubmittingIndex] = useState<number | null>(null);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const isLastEntry = fields.length <= 1;

  const onAdd = () => {
    handleAdd();
    // The new entry is prepended, so keep the top one open.
    setExpandedIndex(0);
  };

  const handleSave = async (index: number) => {
    setSubmittingIndex(index);
    try {
      await submitEntry(index);
    } finally {
      setSubmittingIndex(null);
    }
  };

  const adjustExpandedAfterDelete = (index: number) => {
    setExpandedIndex(prev => {
      if (prev === null) return null;
      if (prev === index) return null;
      return prev > index ? prev - 1 : prev;
    });
  };

  const requestDelete = async (index: number) => {
    const entry = formMethods.getValues(`consents.${index}`);
    if (entry?.id) {
      setPendingDeleteIndex(index);
    } else {
      await deleteEntry(index);
      adjustExpandedAfterDelete(index);
    }
  };

  const confirmDelete = async () => {
    if (pendingDeleteIndex === null) return;
    setIsDeleting(true);
    try {
      await deleteEntry(pendingDeleteIndex);
      adjustExpandedAfterDelete(pendingDeleteIndex);
      setPendingDeleteIndex(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const { isValid } = formMethods.formState;

  return (
    <FormProvider {...formMethods}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 500px)',
          overflowY: 'auto',
          paddingTop: 1,
        }}
      >
        <Button
          variant='text'
          startIcon={<Add />}
          onClick={onAdd}
          disabled={disabled || !canAdd}
          sx={{ alignSelf: 'flex-start', marginBottom: 1 }}
        >
          {tr('addConsent')}
        </Button>

        {fields.map((field, index) => (
          <Box key={field.id}>
            {index > 0 && <Divider />}
            <Accordion
              expanded={expandedIndex === index}
              onChange={() =>
                setExpandedIndex(prev => (prev === index ? null : index))
              }
              disableGutters
              elevation={0}
              sx={{
                border: 'none',
                boxShadow: 'none',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
                <Typography variant='body2' sx={{ fontWeight: 'medium' }}>
                  {tr('consentAccordion', { number: fields.length - index })}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0, pt: 0, pb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    pt: 1,
                  }}
                >
                  <ConsentEntry
                    index={index}
                    programName={programName}
                    disabled={disabled}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant='contained'
                      color='secondary'
                      onClick={() => handleSave(index)}
                      loading={submittingIndex === index}
                      loadingPosition='start'
                      disabled={disabled || !isValid || !dirtyEntries[index]}
                      sx={{ color: 'white' }}
                    >
                      {tr('save')}
                    </Button>
                    <Button
                      variant='text'
                      color='error'
                      onClick={() => requestDelete(index)}
                      disabled={
                        disabled || isLastEntry || submittingIndex === index
                      }
                      sx={{ px: 0 }}
                    >
                      {tr('delete')}
                    </Button>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
          paddingY: 3,
          backgroundColor: theme.palette.common.white,
          position: 'sticky',
          bottom: 0,
          left: 0,
          zIndex: 9,
        }}
      >
        <Button
          variant='contained'
          color='secondary'
          onClick={onClose}
          disabled={isSubmitting}
          sx={{
            fontSize: '0,875rem',
            fontWeight: 'medium',
            lineHeight: '24px',
            color: 'white',
          }}
        >
          {t('general.close')}
        </Button>
      </Box>

      <ConfirmationModal
        isOpen={pendingDeleteIndex !== null}
        title={tr('deleteTitle')}
        message={tr('deleteMessage')}
        confirmText={tr('delete')}
        isLoading={isDeleting}
        onConfirm={confirmDelete}
        onClose={() => setPendingDeleteIndex(null)}
      />
    </FormProvider>
  );
};
